import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Building2, Hash } from 'lucide-react';
import LegalAgreementView, { recordAgreement, toastAgreementError } from '../../components/legal/LegalAgreementView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Final step of the venue onboarding flow. The previous step (Step 7 —
 * photos) writes the complete draft into ``sessionStorage.venueOnboarding``
 * INCLUDING the uploaded photo URLs and intro video URL, then navigates
 * here. We render the venue partner agreement, and only on accept do we
 * actually submit the application to the backend + record the agreement.
 */
export default function VenueOnboardingAgreement() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  // Read the onboarding draft once so the "Signing as ..." context block
  // and the submission payload reference identical values.
  const draft = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem('venueOnboarding') || '{}'); }
    catch { return {}; }
  }, []);

  const signerFullName = [draft.firstName, draft.lastName].filter(Boolean).join(' ').trim();
  const signerBusiness = draft.legalBusinessName || draft.restaurantName || '';
  const signerBlock = (signerFullName || signerBusiness || draft.businessVat) ? (
    <div>
      <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 11.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)' }}>
        Signing as
      </p>
      {signerFullName ? (
        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
          <User size={14} color="var(--text-2)" strokeWidth={2.1} />
          <span data-testid="venue-agreement-signer-name" style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{signerFullName}</span>
        </div>
      ) : null}
      {signerBusiness ? (
        <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Building2 size={14} color="var(--text-2)" strokeWidth={2.1} />
          <span data-testid="venue-agreement-signer-business" style={{ fontFamily: "'Inria Serif', serif", fontSize: 13.5, color: 'var(--text-2)' }}>{signerBusiness}</span>
        </div>
      ) : null}
      {draft.businessVat ? (
        <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Hash size={14} color="var(--text-2)" strokeWidth={2.1} />
          <span data-testid="venue-agreement-signer-vat" style={{ fontFamily: "'Inria Serif', serif", fontSize: 13, color: 'var(--text-3)', letterSpacing: '0.03em' }}>ΑΦΜ {draft.businessVat}</span>
        </div>
      ) : null}
    </div>
  ) : null;

  const handleAccept = async (version, extras = {}) => {
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const allData = JSON.parse(sessionStorage.getItem('venueOnboarding') || '{}');

      // Photos + intro video are collected AFTER the quick onboarding
      // now — the venue owner submits the "lite" application here and
      // completes their media pack from inside the venue app. The backend
      // marks the application as `setup_mode` when photos are absent so
      // it doesn't hit the admin review queue prematurely.

      // 1) Submit the venue application.
      const submitRes = await axios.post(
        `${API}/venue/applications`,
        {
          first_name: allData.firstName || '',
          last_name: allData.lastName || '',
          phone_country_code: allData.phoneCountry?.code || '+30',
          phone: allData.phone || '',
          contact_email: allData.email || '',
          restaurant_name: allData.restaurantName || '',
          legal_business_name: allData.legalBusinessName || '',
          business_vat: allData.businessVat || '',
          country: allData.country || '',
          region: allData.region || '',
          city: allData.city || '',
          zip_code: allData.zipCode || '',
          street: allData.street || '',
          coordinates: allData.coordinates || '',
          website: allData.website || '',
          instagram: allData.instagram || '',
          facebook: allData.facebook || '',
          tiktok: allData.tiktok || '',
          venue_type: allData.venueType || '',
          venue_categories: Array.isArray(allData.venueCategories) ? allData.venueCategories : [],
          venue_tags: Array.isArray(allData.venueTags) ? allData.venueTags : [],
          customer_phone: allData.customerPhone || '',
          operating_hours: allData.operatingHours || {},
          photo_urls: Array.isArray(allData.photoUrls) ? allData.photoUrls : [],
          intro_video_url: allData.introVideoUrl || '',
          step: 4,
          submitted: true,
          invited_referral_id: allData.invitedVenueReferralId || '',
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );

      const venueAppId = submitRes?.data?.id || allData.applicationId || '';

      // 2) Record the accepted agreement against this application. We
      // pass the legal signer identity captured on Step 1 (full first
      // + last name) and the AADE-verified business ΑΦΜ so the stored
      // agreement + generated PDF stamp who signed for which venue.
      try {
        await recordAgreement({
          kind: 'venue',
          version,
          entity_id: venueAppId,
          entity_name: allData.restaurantName || '',
          region: allData.region || '',
          country: allData.country || '',
          city: allData.city || '',
          signer_first_name: allData.firstName || '',
          signer_last_name: allData.lastName || '',
          signer_business_vat: allData.businessVat || '',
          accepted_agreement: extras.accepted_agreement !== false,
          accepted_english_comprehension: !!extras.accepted_english_comprehension,
        });
      } catch (err) {
        // The application is in already; surface the issue but allow the
        // user to proceed — admin can attach the agreement later from the
        // dashboard. (Better UX than rolling back an entire venue app.)
        toastAgreementError(err);
      }

      // Refresh the local user object so the router lands them on the
      // right shell. When the owner submits without the media pack the
      // backend sets `venue_status: 'setup_mode'`; when they already have
      // 10+ photos it flips to `pending`. Either way we need the fresh
      // value in localStorage before we redirect.
      let refreshedStatus = null;
      try {
        const meRes = await axios.get(`${API}/auth/me`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (meRes?.data) {
          localStorage.setItem('user', JSON.stringify(meRes.data));
          refreshedStatus = meRes.data.venue_status || null;
        }
      } catch { /* Non-fatal — fall through to the default landing. */ }

      if (refreshedStatus === 'setup_mode') {
        // Skip the "Application in Progress" screen — send the owner
        // straight into the real venue app (bottom nav + analytics) so
        // they can explore it. A banner on the home page nudges them to
        // finish the checklist (photos/payout) whenever they're ready.
        toast.success('Welcome to your venue app! Finish the checklist to go live.');
        sessionStorage.removeItem('venueOnboarding');
        navigate('/venue/analytics', { replace: true });
      } else {
        toast.success('Application submitted!');
        navigate('/venue/onboarding/complete');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to submit. Please try again.';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <LegalAgreementView
      kind="venue"
      actionLabel="Agree & submit application"
      onAccept={handleAccept}
      onBack={() => navigate('/venue/onboarding/2')}
      busy={busy}
      testIdPrefix="venue-agreement"
      signerBlock={signerBlock}
    />
  );
}
